import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, InputAdornment, Button } from '@mui/material';
import { Search } from '@mui/icons-material';
import { CameraWithPricing } from '../../utils/productDetailHelpers';
import { validSpecKeys, getSpecValue, categorizeSpecKeys, formatSpecLabel, formatSpecValue } from '../../utils/specFormatting';
import { computeSpecIssues, specIssueKind, SPEC_ISSUE_PHRASES } from './specIssues';
import type { SpecIssueKind } from './specIssues';
import { NeutralNote } from './NeutralNote';
import { FieldSourceBadge } from './FieldSourceBadge';
import { tokens } from '../../theme/tokens';

interface ProductSpecificationsTabProps {
  product: CameraWithPricing;
}

export function ProductSpecificationsTab({ product }: ProductSpecificationsTabProps) {
  const [query, setQuery] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);

  const specifications = product.specifications ?? {};

  const { groups, allSpecs, allIssues, shown, issuePhrases, copyLines } = useMemo(() => {
    const allKeys = validSpecKeys(specifications);
    const categorized = categorizeSpecKeys(allKeys);
    const flags = computeSpecIssues(categorized, (k) => getSpecValue(specifications, k), {
      weight: product.dimensions?.weight,
      weightUnit: product.dimensions?.weightUnit,
    });

    const q = query.trim().toLowerCase();
    let shownCount = 0;
    // Every spec, before the search and issues-only filters — "Copy all" means
    // all of them, not just the rows those filters left on screen.
    const copyLines: string[] = [];
    const builtGroups = Array.from(categorized.entries()).map(([name, keys]) => {
      const rows = keys
        .map((key) => {
          const raw = getSpecValue(specifications, key);
          if (!raw) return null;
          const value = formatSpecValue(key, raw);
          const label = formatSpecLabel(key);
          const flag = flags.get(key) || null;
          return { key, label, value, flag };
        })
        .filter((s): s is { key: string; label: string; value: string; flag: string | null } => !!s);
      rows.forEach((row) => copyLines.push(`${row.label}: ${row.value}`));
      const specs = rows
        .filter((s) => !issuesOnly || s.flag)
        .filter((s) => !q || (s.label + ' ' + s.value).toLowerCase().includes(q));
      shownCount += specs.length;
      return { name, specs, issueCount: specs.filter((s) => s.flag).length };
    }).filter((g) => g.specs.length > 0);

    // Name only the issue kinds that actually fired, in a stable order.
    const kindOrder: SpecIssueKind[] = ['unmapped-key', 'missing-unit', 'swapped-label', 'cross-field'];
    const firedKinds = new Set(Array.from(flags.values()).map((message) => specIssueKind(message)));
    const issuePhrases = kindOrder.filter((kind) => firedKinds.has(kind)).map((kind) => SPEC_ISSUE_PHRASES[kind]);

    return { groups: builtGroups, allSpecs: allKeys.length, allIssues: flags.size, shown: shownCount, issuePhrases, copyLines };
  }, [specifications, product.dimensions, query, issuesOnly]);

  const handleCopyAll = () => {
    navigator.clipboard.writeText(copyLines.join('\n'));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: 'text.primary' }}>Specifications</Typography>
            <FieldSourceBadge product={product} field="specs" />
          </Box>
          <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted, fontVariantNumeric: 'tabular-nums' }}>
            {shown === allSpecs ? `${allSpecs} specifications` : `${shown} of ${allSpecs} shown`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search specifications"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: tokens.ink.faint }} /></InputAdornment>,
            }}
            sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: '9px', fontSize: '13.5px', height: 34 } }}
          />
          {allIssues > 0 && (
            <Button
              onClick={() => setIssuesOnly((prev) => !prev)}
              startIcon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />}
              sx={{
                height: 34, px: '11px', borderRadius: '9px', border: '1px solid', borderColor: issuesOnly ? tokens.ink.disabled : 'divider',
                bgcolor: issuesOnly ? tokens.surface.tint : 'background.paper', color: issuesOnly ? 'text.primary' : 'text.secondary',
                textTransform: 'none', fontWeight: 600, fontSize: '12.5px', whiteSpace: 'nowrap',
              }}
            >
              {issuesOnly ? `Showing ${allIssues} to check` : `${allIssues} to check`}
            </Button>
          )}
          <Button
            onClick={handleCopyAll}
            sx={{ height: 34, px: '11px', borderRadius: '9px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.secondary', textTransform: 'none', fontWeight: 600, fontSize: '12.5px', whiteSpace: 'nowrap' }}
          >
            Copy all
          </Button>
        </Box>
      </Box>

      {allIssues > 0 && (
        <NeutralNote>
          {allIssues} of {allSpecs} specifications need attention
          {issuePhrases.length > 0
            ? ` — ${issuePhrases.length > 1
                ? `${issuePhrases.slice(0, -1).join(', ')} and ${issuePhrases[issuePhrases.length - 1]}`
                : issuePhrases[0]}`
            : ''}. Each is marked below.
        </NeutralNote>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
        {groups.map((group) => (
          <Box key={group.name} sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.1, pb: 0.9, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.primary' }}>
                {group.name}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>{group.specs.length}</Typography>
              <Box sx={{ flex: 1 }} />
              {group.issueCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontSize: '11.5px', fontWeight: 600, color: tokens.signal.caution.ink }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />
                  <span>{group.issueCount} to check</span>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4 }}>
              {group.specs.map((spec) => (
                <Box key={spec.key} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 1.75 }, alignItems: 'baseline', py: '8px', borderBottom: `1px solid ${tokens.surface.sunken}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                    {spec.flag && <Box title={spec.flag} sx={{ flex: '0 0 auto', width: 5, height: 5, borderRadius: '50%', bgcolor: tokens.signal.caution.solid }} />}
                    <Typography sx={{ fontSize: '12.5px', color: tokens.ink.muted }}>{spec.label}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', lineHeight: 1.5, color: spec.flag ? tokens.signal.caution.ink : 'text.primary' }}>{spec.value}</Typography>
                    {spec.flag && <Typography sx={{ fontSize: '11.5px', lineHeight: 1.45, color: tokens.signal.caution.ink }}>{spec.flag}</Typography>}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
        {groups.length === 0 && (
          <Typography sx={{ fontSize: '13px', color: tokens.ink.faint }}>
            {allSpecs === 0 && !query.trim()
              ? 'No specifications for this product.'
              : 'No specifications match that search.'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
